import type {
  ActivityEventName,
  HelpVideoId,
  ShareType,
  ShareFormat,
} from '@tarrot/shared'
import { apiFetch, getAuthTokenOrNull } from '../services/api'

/**
 * Fire-and-forget product analytics for the batch-3 theme picker.
 *
 * Contract:
 *   - never throws and never blocks the UI (fully async, silent catch)
 *   - no-ops when signed out (no token → no request; the endpoint is authed)
 *
 * The server allowlist-validates the event name and prop shape, so callers must
 * pass one of the registered events with its exact props. Typed props keep the
 * three known events honest at the call site.
 */
type EventProps = {
  theme_previewed: { theme: string; tier: string }
  theme_applied: { theme: string }
  upgrade_clicked: { from: 'appearance' | 'preview_bar' }
  accent_set: { hue: number }
  help_video_played: { id: HelpVideoId; from: 'list' | 'hint' | 'onboarding' }
  help_video_completed: { id: HelpVideoId }
  hint_opened: { id: HelpVideoId; screen: string }
  help_feedback: { id: HelpVideoId; helpful: boolean }
  share_opened: { type: ShareType }
  share_completed: { type: ShareType; format: ShareFormat; question_included: boolean }
  share_link_visited: { shortid: string }
  referral_signup: { code: string }
  referral_activated: { code: string }
  practitioner_attribution: { deck: string }
}

export function track<E extends ActivityEventName>(
  event: E,
  props: EventProps[E],
): void {
  // Deliberately not awaited: analytics must never delay or block a click.
  void (async () => {
    try {
      const token = await getAuthTokenOrNull()
      if (!token) return // signed out — no-op, the endpoint requires auth
      await apiFetch('/activity/events', {
        method: 'POST',
        body: JSON.stringify({ event, props }),
      })
    } catch {
      // Swallow everything — a failed analytics ping must be invisible.
    }
  })()
}
